import React, { useState, useEffect, useCallback } from "react";
import {
  fetchOptimizerSguGeneration,
  OptimizerSguUpdateData,
} from "../../../../../Rest_api/DemoRestApi";
import loader from "../../../../../components/recharts/loader.gif";
import { useSelector, useDispatch } from "react-redux";
import { OptimizierDataUpdate } from "../../../../../store/state/GNAOptimizer/CurrentOptimisationState";
import Loading from "../../../../../components/Loading";

interface ScenarioState {
  sguDeclared: boolean;
  sguScheduled: boolean;
  includeOutage: boolean;
  sguUtilisation: number;
  sguScenario: string;
  reUtilisation: number;
  reScenario: string;
  isgsUtilisation: number;
  isgsScenario: string;
}

interface SimulationScenarioProps {
  onApply: (data: ScenarioState) => void;
  onReset: () => void;
  loading: boolean;
}

// Slider configuration for percentage fields
const sliderConfig = {
  sguUtilisation: { min: 0, max: 100, step: 0.1, unit: "%" },
  reUtilisation: { min: 0, max: 100, step: 0.1, unit: "%" },
  isgsUtilisation: { min: 0, max: 100, step: 0.1, unit: "%" },
};

export function SimulationScenario({
  onApply,
  onReset,
  loading,
}: SimulationScenarioProps) {
  const OptimizerData = useSelector(
    (state: any) => state.optimizierData.data.optimizerData
  );

  // Helper function to get initial state from OptimizerData
  const getInitialState = useCallback((): ScenarioState => {
    if (!OptimizerData) {
      return {
        sguDeclared: false,
        sguScheduled: false,
        includeOutage: false,
        sguUtilisation: 0,
        sguScenario: "",
        reUtilisation: 0,
        reScenario: "",
        isgsUtilisation: 0,
        isgsScenario: "",
      };
    }
    return {
      sguDeclared: OptimizerData.sguDeclared ?? false,
      sguScheduled: OptimizerData.sguScheduled ?? false,
      includeOutage: OptimizerData.includeOutage ?? false,
      sguUtilisation: OptimizerData.sguUtilisation ?? 0,
      sguScenario: OptimizerData.sguScenario ?? "",
      reUtilisation: OptimizerData.reUtilisation ?? 0,
      reScenario: OptimizerData.reScenario ?? "",
      isgsUtilisation: OptimizerData.isgsUtilisation ?? 0,
      isgsScenario: OptimizerData.isgsScenario ?? "",
    };
  }, [OptimizerData]);

  const [formData, setFormData] = useState<ScenarioState>(getInitialState());

  // Update form data when OptimizerData changes
  useEffect(() => {
    if (OptimizerData) {
      setFormData(getInitialState());
    }
  }, [OptimizerData, getInitialState]);

  const handleCapacityChange = (field: "sguDeclared" | "sguScheduled") => {
    setFormData((prev) => ({
      ...prev,
      sguDeclared: field === "sguDeclared",
      sguScheduled: field === "sguScheduled",
    }));
  };

  const handleToggleChange = () => {
    setFormData((prev) => ({
      ...prev,
      includeOutage: !prev.includeOutage,
    }));
  };

  const handleUtilisationChange = (
    field: "sguUtilisation" | "reUtilisation" | "isgsUtilisation",
    value: number
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleScenarioChange = (
    field: "sguScenario" | "reScenario" | "isgsScenario",
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleReset = () => {
    setFormData(getInitialState());
    onReset();
  };

  const handleApply = () => {
    onApply(formData);
  };

  return (
    <div className="p-0 w-full h-full">
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #F1935C;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #F1935C;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>

      {/* State Generating Unit Scenario */}
      <h3 className="text-base text-[#34656d] font-medium mb-4">
        State Generating Unit Scenario
      </h3>

      <div className="mb-8">
        {/* Declared Capacity Checkbox */}
        <div
          className="flex items-center gap-2 mb-4 cursor-pointer"
          onClick={() => handleCapacityChange("sguDeclared")}
        >
          <div
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              formData.sguDeclared
                ? "bg-orange-500 border-orange-500"
                : "border-gray-300"
            }`}
          >
            {formData.sguDeclared && (
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path
                  d="M1.5 5L4 7.5L8.5 2"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
          <span
            className={`${
              formData.sguDeclared
                ? "text-sm text-[#F1935C]"
                : "text-sm text-[#8C8A96]"
            }`}
          >
            Declared Capacity
          </span>
        </div>

        {/* Scheduled Generation Checkbox */}
        <div
          className="flex items-center gap-2 mb-4 cursor-pointer"
          onClick={() => handleCapacityChange("sguScheduled")}
        >
          <div
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              formData.sguScheduled
                ? "bg-orange-500 border-orange-500"
                : "border-gray-300"
            }`}
          >
            {formData.sguScheduled && (
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path
                  d="M1.5 5L4 7.5L8.5 2"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
          <span
            className={`${
              formData.sguScheduled
                ? "text-sm text-[#F1935C]"
                : "text-sm text-[#8C8A96]"
            }`}
          >
            Scheduled Generation
          </span>
        </div>

        {/* Include Outage Toggle */}
        <div className="flex items-center justify-start mb-8 gap-3">
          <label className="text-sm text-[#8C8A96]">Include Outage</label>
          <button
            type="button"
            onClick={handleToggleChange}
            className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors focus:outline-none ${
              formData.includeOutage ? "bg-orange-500" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block w-5 h-5 transform bg-white rounded-full shadow transition-transform ${
                formData.includeOutage ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>

        {/* Utilisation Slider */}
        <div className="mb-4">
          <div className="flex justify-between items-center gap-4">
            <label className="text-sm text-[#6F6D7C] font-medium mb-0">
              Utilisation
            </label>

            <div className="flex items-center justify-between gap-2 w-full">
              <span className="text-xs text-gray-400 mt-1">
                {sliderConfig.sguUtilisation.min}
              </span>
              <div className="relative w-full">
                <div
                  className="absolute -top-7 text-xs font-semibold text-[#F1935C] bg-[#FFF4ED] p-2 rounded-lg"
                  style={{
                    left: `${formData.sguUtilisation}%`,
                    transform: "translateX(-50%)",
                  }}
                >
                  {formData.sguUtilisation}%
                </div>

                <input
                  type="range"
                  min={sliderConfig.sguUtilisation.min}
                  max={sliderConfig.sguUtilisation.max}
                  step={sliderConfig.sguUtilisation.step}
                  value={formData.sguUtilisation}
                  onChange={(e) =>
                    handleUtilisationChange(
                      "sguUtilisation",
                      parseInt(e.target.value)
                    )
                  }
                  className="w-full h-2 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #F1935C ${formData.sguUtilisation}%, #e5e7eb ${formData.sguUtilisation}%)`,
                  }}
                />
              </div>

              <span className="text-xs text-gray-400 mt-1">
                {sliderConfig.sguUtilisation.max}%
              </span>
            </div>
          </div>
        </div>

        {/* Scenario Select */}
        <div className="relative flex flex-row gap-4 items-center">
          <label className="text-sm text-[#6F6D7C] font-medium block">
            Scenario
          </label>
          <select
            value={formData.sguScenario}
            onChange={(e) =>
              handleScenarioChange("sguScenario", e.target.value)
            }
            className="w-full py-1 px-4 pr-10 border border-gray-200 rounded-lg text-sm text-gray-400 bg-white appearance-none outline-none cursor-pointer"
          >
            <option value="">Select Scenario</option>
            <option value="1">Current year</option>
            <option value="2">Past years</option>
            <option value="3">Past 2 years</option>
          </select>
        </div>
      </div>

      {/* Renewable Energy Scenario */}
      <div className="mb-8">
        <h3 className="text-base text-[#34656d] font-medium mb-8">
          Renewable Energy Scenario
        </h3>

        <div className="mb-5">
          <div className="flex justify-between items-center gap-4">
            <label className="text-sm text-[#6F6D7C] font-medium mb-0">
              Utilisation
            </label>

            <div className="flex items-center justify-between gap-2 w-full">
              <span className="text-xs text-gray-400 mt-1">
                {sliderConfig.reUtilisation.min}
              </span>
              <div className="relative w-full">
                <div
                  className="absolute -top-7 text-xs font-semibold text-[#F1935C] bg-[#FFF4ED] p-2 rounded-lg"
                  style={{
                    left: `${formData.reUtilisation}%`,
                    transform: "translateX(-50%)",
                  }}
                >
                  {formData.reUtilisation}%
                </div>

                <input
                  type="range"
                  min={sliderConfig.reUtilisation.min}
                  max={sliderConfig.reUtilisation.max}
                  step={sliderConfig.reUtilisation.step}
                  value={formData.reUtilisation}
                  onChange={(e) =>
                    handleUtilisationChange(
                      "reUtilisation",
                      parseInt(e.target.value)
                    )
                  }
                  className="w-full h-2 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #F1935C ${formData.reUtilisation}%, #e5e7eb ${formData.reUtilisation}%)`,
                  }}
                />
              </div>

              <span className="text-xs text-gray-400 mt-1">
                {sliderConfig.reUtilisation.max}%
              </span>
            </div>
          </div>
        </div>

        <div className="relative flex flex-row gap-4 items-center">
          <label className="text-sm text-[#6F6D7C] font-medium block">
            Scenario
          </label>
          <select
            value={formData.reScenario}
            onChange={(e) => handleScenarioChange("reScenario", e.target.value)}
            className="w-full py-1 px-4 pr-10 border border-gray-200 rounded-lg text-sm text-gray-400 bg-white appearance-none outline-none cursor-pointer"
          >
            <option value="">Select Scenario</option>
            <option value="1">Current year</option>
            <option value="2">Past years</option>
            <option value="3">Past 2 years</option>
          </select>
        </div>
      </div>

      {/* ISGS Scenario */}
      <div className="mb-2">
        <h3 className="text-base text-[#34656d] font-medium mb-8">
          ISGS Scenario
        </h3>

        <div className="mb-2">
          <div className="flex justify-between items-center gap-4">
            <label className="text-sm text-[#6F6D7C] font-medium mb-0">
              Utilisation
            </label>
            <div className="flex items-center justify-between gap-2  w-full">
              <span className="text-xs text-gray-400 mt-1">
                {sliderConfig.isgsUtilisation.min}
              </span>
              <div className="relative w-full">
                <div
                  className="absolute -top-7 text-xs font-semibold text-[#F1935C] bg-[#FFF4ED] p-2 rounded-lg"
                  style={{
                    left: `${formData.isgsUtilisation}%`,
                    transform: "translateX(-50%)",
                  }}
                >
                  {formData.isgsUtilisation}%
                </div>

                <input
                  type="range"
                  min={sliderConfig.isgsUtilisation.min}
                  max={sliderConfig.isgsUtilisation.max}
                  step={sliderConfig.isgsUtilisation.step}
                  value={formData.isgsUtilisation}
                  onChange={(e) =>
                    handleUtilisationChange(
                      "isgsUtilisation",
                      parseInt(e.target.value)
                    )
                  }
                  className="w-full h-2 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #F1935C ${formData.isgsUtilisation}%, #e5e7eb ${formData.isgsUtilisation}%)`,
                  }}
                />
              </div>

              <span className="text-xs text-gray-400 mt-1">
                {sliderConfig.isgsUtilisation.max}%
              </span>
            </div>
          </div>
        </div>

        <div className="relative flex flex-row gap-4 items-center">
          <label className="text-sm text-[#6F6D7C] font-medium block">
            Scenario
          </label>
          <select
            value={formData.isgsScenario}
            onChange={(e) =>
              handleScenarioChange("isgsScenario", e.target.value)
            }
            className="w-full py-1 px-4 pr-10 border border-gray-200 rounded-lg text-sm text-gray-400 bg-white appearance-none outline-none cursor-pointer"
          >
            <option value="">Select Scenario</option>
            <option value="1">Current year</option>
            <option value="2">Past years</option>
            <option value="3">Past 2 years</option>
          </select>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-2 py-6">
        <button
          onClick={handleReset}
          className="w-[171px] h-[42px] text-base font-medium text-[#6F6D7C] bg-transparent border-none rounded-lg cursor-pointer hover:text-gray-700 transition-colors"
        >
          Reset
        </button>
        <button
          onClick={handleApply}
          className="w-[171px] h-[42px] text-base font-medium text-white bg-[#F1935C] rounded-lg cursor-pointer shadow-md shadow-orange-500/20 hover:bg-[#cd733f] transition-colors"
        >
          {loading ? "Loading..." : "Apply"}
        </button>
      </div>
    </div>
  );
}

// Main SimulationTable component
export default function SimulationPortfolio({
  open,
  setOpen,
  setError,
  refreshKey,
  setRefreshKey,
}: {
  open: boolean;
  setOpen: any;
  setError?: any;
  refreshKey: any;
  setRefreshKey: any;
}) {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const handleApply = async (scenarioData: ScenarioState) => {
    setLoading(true);

    try {
      const payload = scenarioData;
      await OptimizerSguUpdateData(payload);
      setRefreshKey((prev: number) => prev + 1);
      (window as any).resetNextTabs?.("Generation Portfolio");
    } catch (error) {
      console.error("Error submitting data", error);
      setError("Error submitting data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const SguData = await fetchOptimizerSguGeneration();
        // Store data in Redux instead of sessionStorage
        dispatch(OptimizierDataUpdate({ data: SguData }));
      } catch (err) {
        console.error("Error fetching optimizer SGU generation", err);
        if (setError) setError("Error fetching data");
      }
    };
    fetchData();
  }, [refreshKey, dispatch, setError]);

  const handleReset = () => {};

  return (
    <div
      className="gas-chart-container simulation_table shadow-[rgba(149,157,165,0.2)_0px_8px_24px] bg-white overflow-y-auto overflow-hidden"
      id="slide"
      style={{
        width: "22%",
        height: "calc(-195px + 100vh)",
        transition: "all 0.8s ease-out",
        paddingRight: open ? "1rem" : "12px",
      }}
    >
      {loading && <Loading />}
      <SimulationScenario
        loading={loading}
        onApply={handleApply}
        onReset={handleReset}
      />
    </div>
  );
}
